// Pure math for the free Mortgage & Affordability calculator. No imports
// from anywhere else — deliberately duplicated from existing /free-tools
// math patterns, not reused.

const n = (v) => {
  const parsed = Number(v);
  return Number.isFinite(parsed) ? parsed : 0;
};

const finiteOr = (x, fallback = 0) =>
  Number.isFinite(x) ? x : fallback;

export const DTI_TARGET = 36; // percent

export function normalizeCdd(amount, schedule) {
  const a = n(amount);
  switch (schedule) {
    case 'Quarterly':
      return a / 3;
    case 'Annually':
      return a / 12;
    case 'Monthly':
    default:
      return a;
  }
}

// Standard amortized monthly payment for principal & interest.
function piPayment(loanAmount, monthlyRate, numPayments) {
  if (loanAmount <= 0 || numPayments <= 0) return 0;
  if (monthlyRate <= 0) return loanAmount / numPayments;
  const factor = Math.pow(1 + monthlyRate, numPayments);
  return loanAmount * (monthlyRate * factor) / (factor - 1);
}

export function computeMortgageAffordability(inputs = {}) {
  const homePrice = n(inputs.homePrice);
  const downPaymentRaw = n(inputs.downPayment);
  const downPaymentType = inputs.downPaymentType === 'percent' ? 'percent' : 'dollar';
  const interestRate = n(inputs.interestRate);
  const termYears = n(inputs.termYears);
  const propertyTaxes = n(inputs.propertyTaxes);
  const taxType = inputs.taxType === 'percent' ? 'percent' : 'dollar';
  const insurance = n(inputs.insurance);
  const pmiRate = n(inputs.pmiRate);
  const hoaMonthly = n(inputs.hoaMonthly);
  const cddAmount = n(inputs.cddAmount);
  const cddSchedule = inputs.cddSchedule || 'Monthly';
  const grossMonthlyIncome = n(inputs.grossMonthlyIncome);
  const otherMonthlyDebt = n(inputs.otherMonthlyDebt);

  const downPaymentAmount = downPaymentType === 'percent'
    ? homePrice * (downPaymentRaw / 100)
    : downPaymentRaw;

  const loanAmount = Math.max(0, homePrice - downPaymentAmount);
  const ltv = homePrice > 0 ? (loanAmount / homePrice) * 100 : 0;

  const monthlyRate = (interestRate / 100) / 12;
  const numPayments = termYears * 12;

  const principalInterest = finiteOr(piPayment(loanAmount, monthlyRate, numPayments));

  const taxesMonthly = finiteOr(
    taxType === 'percent'
      ? (homePrice * (propertyTaxes / 100)) / 12
      : propertyTaxes / 12
  );
  const insuranceMonthly = finiteOr(insurance / 12);
  const pmiMonthly = ltv > 80 ? finiteOr((loanAmount * (pmiRate / 100)) / 12) : 0;
  const cddMonthly = finiteOr(normalizeCdd(cddAmount, cddSchedule));

  const piti = principalInterest + taxesMonthly + insuranceMonthly + pmiMonthly + hoaMonthly + cddMonthly;

  // Affordability — only when income is provided.
  let qualified = null;
  let maxAllowedPITI = null;
  let dti = null;
  let maxAffordablePrice = null;

  if (grossMonthlyIncome > 0) {
    maxAllowedPITI = grossMonthlyIncome * (DTI_TARGET / 100) - otherMonthlyDebt;
    qualified = piti <= maxAllowedPITI;
    dti = ((piti + otherMonthlyDebt) / grossMonthlyIncome) * 100;

    const nonPI = taxesMonthly + insuranceMonthly + pmiMonthly + hoaMonthly + cddMonthly;
    const availableForPI = maxAllowedPITI - nonPI;
    if (availableForPI > 0 && monthlyRate > 0 && numPayments > 0) {
      const factor = Math.pow(1 + monthlyRate, numPayments);
      const annuityFactor = (monthlyRate * factor) / (factor - 1);
      const maxLoanAmount = availableForPI / annuityFactor;
      maxAffordablePrice = finiteOr(maxLoanAmount + downPaymentAmount, null);
    } else if (availableForPI > 0 && monthlyRate === 0 && numPayments > 0) {
      // Edge case: 0% rate — straight-line amortization
      const maxLoanAmount = availableForPI * numPayments;
      maxAffordablePrice = finiteOr(maxLoanAmount + downPaymentAmount, null);
    }
  }

  const breakdown = [
    { key: 'pi',        label: 'Principal & interest',     amount: principalInterest },
    { key: 'taxes',     label: 'Property taxes',           amount: taxesMonthly },
    { key: 'insurance', label: 'Homeowners insurance',     amount: insuranceMonthly },
    { key: 'pmi',       label: 'PMI',                      amount: pmiMonthly },
    { key: 'hoa',       label: 'HOA',                      amount: hoaMonthly },
    { key: 'cdd',       label: 'CDD',                      amount: cddMonthly },
  ].filter((it) => it.amount > 0);

  return {
    downPaymentAmount: finiteOr(downPaymentAmount),
    loanAmount: finiteOr(loanAmount),
    ltv: finiteOr(ltv),
    principalInterest,
    taxesMonthly,
    insuranceMonthly,
    pmiMonthly,
    hoaMonthly: finiteOr(hoaMonthly),
    cddMonthly,
    piti: finiteOr(piti),
    qualified,
    maxAllowedPITI: maxAllowedPITI === null ? null : finiteOr(maxAllowedPITI, null),
    dti: dti === null ? null : finiteOr(dti, null),
    maxAffordablePrice,
    breakdown,
  };
}

export function fmtUSD(num, opts = {}) {
  const { cents = false } = opts;
  if (num == null || Number.isNaN(num)) return '—';
  const abs = Math.abs(num);
  const str =
    '$' +
    abs.toLocaleString('en-US', {
      minimumFractionDigits: cents ? 2 : 0,
      maximumFractionDigits: cents ? 2 : 0,
    });
  return (num < 0 ? '−' : '') + str;
}
